const DEFAULT_POST_LOGIN_PATH = '/dashboard';
const RETURN_TO_PARAM = 'returnTo';
// Must include every route that redirects authenticated users away or redirects unauthenticated
// users back through login. Out-of-sync entries can create soft post-login loops.
const AUTH_ROUTE_PATHS = new Set(['/login', '/signup', '/logout']);
const DISALLOWED_PREFIXES = ['/api', '/trpc'];
const ALLOWED_PATH_PREFIXES = [
  '/dashboard',
  '/ownerships',
  '/properties',
  '/associations',
  '/documents',
  '/document',
  '/contracts',
  '/contract',
  '/rfp',
  '/proposal',
  '/comparison',
  '/budgets',
  '/budget-planning',
  '/capital-management',
  '/ar-invoices',
  '/invoices',
  '/invoice',
  '/calendar',
  '/account',
  '/upload',
  '/users',
  '/notifications',
  '/admin',
  '/incomescheduler',
  '/paymentsExample',
  '/privacy-policy',
  '/terms-of-use',
] as const;

function hasBlockedPrefix(pathname: string): boolean {
  return DISALLOWED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function hasAllowedPrefix(pathname: string): boolean {
  return ALLOWED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function normalizeReturnToCandidate(returnTo: string): string | null {
  if (!returnTo.startsWith('/') || returnTo.startsWith('//') || returnTo.includes('\\')) {
    return null;
  }

  try {
    const url = new URL(returnTo, 'http://localhost');

    if (
      url.origin !== 'http://localhost' ||
      AUTH_ROUTE_PATHS.has(url.pathname) ||
      hasBlockedPrefix(url.pathname) ||
      !hasAllowedPrefix(url.pathname)
    ) {
      return null;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function getSafePostLoginDestination(
  search: string,
  fallback = DEFAULT_POST_LOGIN_PATH,
): string {
  const returnTo = new URLSearchParams(search).get(RETURN_TO_PARAM);
  if (!returnTo) {
    return fallback;
  }

  return normalizeReturnToCandidate(returnTo) ?? fallback;
}

export function buildLoginRedirectPath(currentPath: string): string {
  const params = new URLSearchParams({
    [RETURN_TO_PARAM]: currentPath,
  });

  return `/login?${params.toString()}`;
}

export const __private = {
  ALLOWED_PATH_PREFIXES,
  DEFAULT_POST_LOGIN_PATH,
  DISALLOWED_PREFIXES,
  RETURN_TO_PARAM,
  hasAllowedPrefix,
  hasBlockedPrefix,
  normalizeReturnToCandidate,
};
